import { FC, useCallback, useEffect, useState } from "react";

import { Alert, Button, Stack, TextField } from "@mui/material";
import {
  getSubregion,
  getSubregionArea,
  patchSubregion,
  ResolutionTier,
  SubregionModel,
} from "@rubi/api-generated";
import { Link, useParams } from "react-router-dom";

import { Crumb, DevPage, ErrorMsg, Section } from "./common";
import {
  ProcessingRunLink,
  RegionAreaMapSection,
  RegionBreadcrumbs,
} from "./regionShared";

export const DevSubregionPage: FC = () => {
  const { orgId = "", subregionId = "" } = useParams();

  const [subregion, setSubregion] = useState<SubregionModel | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [metadataJson, setMetadataJson] = useState("");
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const load = async () => {
    try {
      const subregionResponse = await getSubregion({
        path: { org_id: orgId, subregion_id: subregionId },
      });
      const sr = subregionResponse.data ?? null;
      setSubregion(sr);
      setMetadataJson(JSON.stringify(sr?.metadata ?? {}, null, 2));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load Subregion");
    }
  };

  useEffect(() => {
    load();
  }, [orgId, subregionId]);

  const fetchArea = useCallback(
    async (tier: ResolutionTier) =>
      (
        await getSubregionArea({
          path: { org_id: orgId, subregion_id: subregionId, tier },
        })
      ).data,
    [orgId, subregionId],
  );

  const handleSaveMetadata = async () => {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(metadataJson);
    } catch {
      setMetadataError("Not valid JSON");
      return;
    }
    setMetadataError(null);
    setSaving(true);
    try {
      const response = await patchSubregion({
        path: { org_id: orgId, subregion_id: subregionId },
        body: { metadata: parsed },
      });
      setSubregion(response.data ?? null);
      setSaveSuccess(true);
    } catch (e) {
      setMetadataError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DevPage
      title={`Subregion ${subregionId}`}
      breadcrumbs={
        <RegionBreadcrumbs orgId={orgId}>
          <Crumb>
            <Link to={`/dev_admin/region/${orgId}/subregion`}>Subregions</Link>
          </Crumb>
          <Crumb>{subregionId}</Crumb>
        </RegionBreadcrumbs>
      }
    >
      <Section subtitle="Details">
        <ErrorMsg msg={error} />
        <table className="dev-table dev-table--details">
          <tbody>
            <tr>
              <td>id</td>
              <td className="mono">{subregion?.id}</td>
            </tr>
            <tr>
              <td>processing run</td>
              <td className="mono">
                <ProcessingRunLink
                  orgId={orgId}
                  runId={subregion?.geo_data_file_processing_run_id}
                />
              </td>
            </tr>
            <tr>
              <td>time_created</td>
              <td className="mono muted">{subregion?.time_created}</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section subtitle="Metadata">
        <Stack spacing={2} sx={{ maxWidth: 600 }}>
          <TextField
            fullWidth
            multiline
            minRows={6}
            value={metadataJson}
            onChange={(e) => setMetadataJson(e.target.value)}
            error={!!metadataError}
            helperText={metadataError}
            slotProps={{
              htmlInput: { style: { fontFamily: "monospace", fontSize: 13 } },
            }}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSaveMetadata}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save metadata"}
            </Button>
          </Stack>
        </Stack>
      </Section>

      <RegionAreaMapSection fetchArea={fetchArea} />

      <Alert
        severity="success"
        sx={{ display: saveSuccess ? "flex" : "none", mt: 2 }}
        onClose={() => setSaveSuccess(false)}
      >
        Saved
      </Alert>
    </DevPage>
  );
};
